import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useStore } from "@/lib/theme";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Trash2, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { inr } from "@/lib/format";

export default function StorefrontCheckout() {
  const { slug } = useParams();
  const { user } = useAuth();
  const { tenant } = useStore();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [coupon, setCoupon] = useState("");
  const [usePoints, setUsePoints] = useState(0);
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState(user?.phone || "");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [placedOrder, setPlacedOrder] = useState(null);
  const [rewards, setRewards] = useState({ points: 0 });

  const readCart = () => {
    try {
      const raw = localStorage.getItem(`cart_${slug}`);
      setItems(raw ? JSON.parse(raw) : []);
    } catch { setItems([]); }
  };
  useEffect(() => {
    readCart();
    if (user?.role === "customer") api.get("/customer/rewards").then(({ data }) => setRewards(data)).catch(() => {});
  }, [slug, user]);

  const remove = (i) => {
    const next = items.filter((_, idx) => idx !== i);
    localStorage.setItem(`cart_${slug}`, JSON.stringify(next));
    setItems(next);
    window.dispatchEvent(new Event("cart_update"));
  };

  const subtotal = items.reduce((s, x) => s + (x.rate || 0) * (x.units || 1) * (x.qty || 1), 0);
  const depositTotal = items.reduce((s, x) => s + (x.deposit || 0), 0);
  const pointsValue = Math.min(usePoints, rewards.points) * (tenant?.point_value_rupee || 1);
  const total = Math.max(0, subtotal - pointsValue) + depositTotal;

  const place = async () => {
    if (!user || user.role !== "customer") {
      toast.error("Please sign in to continue");
      nav(`/t/${slug}/login`);
      return;
    }
    if (!items.length) { toast.error("Cart is empty"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/store/${slug}/orders`, {
        items: items.map((it) => ({
          product_id: it.product_id,
          qty: it.qty,
          duration_type: it.duration_type,
          units: it.units,
          start_date: it.start_date,
          end_date: it.end_date || null,
          is_recurring: !!it.is_recurring,
        })),
        delivery_address: address,
        delivery_phone: phone,
        coupon_code: coupon || null,
        reward_points_to_use: Math.min(usePoints, rewards.points),
        notes,
      });
      // mock pay immediately
      const { data: pay } = await api.post(`/store/${slug}/orders/${data.id}/pay`);
      setPlacedOrder({ ...data, payment: pay.payment });
      localStorage.removeItem(`cart_${slug}`);
      window.dispatchEvent(new Event("cart_update"));
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSubmitting(false);
    }
  };

  const b = tenant?.branding || {};

  if (placedOrder) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12 text-center">
        <Card className="t-card t-surface p-10" data-testid="order-success-card">
          <div className="w-16 h-16 rounded-full grid place-items-center mx-auto mb-4 text-3xl" style={{ background: b.primary_color, color: "#fff" }}>✓</div>
          <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: b.heading_font }}>Booking confirmed!</h2>
          <p className="opacity-70">Order #<span className="font-mono">{placedOrder.id.slice(0, 8)}</span></p>
          <p className="mt-1 text-sm opacity-60">Payment ref (MOCK): <code>{placedOrder.payment?.ref}</code></p>
          <div className="mt-6 flex gap-3 justify-center">
            <Button onClick={() => nav(`/t/${slug}/account`)} style={{ background: b.primary_color, color: "#fff" }} data-testid="order-view-orders-btn">View My Orders</Button>
            <Button variant="outline" onClick={() => nav(`/t/${slug}`)}>Continue Shopping</Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <h1 className="text-3xl font-bold mb-1" style={{ fontFamily: b.heading_font, color: b.primary_color }} data-testid="checkout-title">Checkout</h1>
      <p className="opacity-70 text-sm mb-6">{items.length} item(s) in cart</p>

      {items.length === 0 ? (
        <Card className="t-card t-surface p-10 text-center"><p className="opacity-60">Your cart is empty.</p><Link to={`/t/${slug}`} className="text-sm underline mt-2 inline-block">Continue browsing</Link></Card>
      ) : (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-3">
            {items.map((it, i) => (
              <Card key={i} className="t-card t-surface p-4 flex gap-3 items-center" data-testid={`cart-item-${i}`}>
                {it.product_image && <img src={it.product_image} alt="" className="w-16 h-16 object-cover rounded-md" />}
                <div className="flex-1">
                  <div className="font-medium">{it.product_name}</div>
                  <div className="text-xs opacity-60">{it.duration_type} • {it.units} unit(s) × {it.qty} qty • Start {it.start_date}</div>
                  <div className="text-xs mt-0.5"><b>{inr((it.rate || 0) * (it.units || 1) * (it.qty || 1))}</b> + deposit {inr(it.deposit || 0)}</div>
                </div>
                <Button size="icon" variant="ghost" onClick={() => remove(i)} data-testid={`cart-remove-${i}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
              </Card>
            ))}
            <Card className="t-card t-surface p-4 space-y-3">
              <div><Label>Delivery address</Label><Textarea value={address} onChange={(e) => setAddress(e.target.value)} rows={2} data-testid="checkout-address-input" /></div>
              <div><Label>Contact phone</Label><Input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="checkout-phone-input" /></div>
              <div><Label>Notes (optional)</Label><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} /></div>
            </Card>
          </div>

          <Card className="t-card t-surface p-5 h-fit space-y-3 sticky top-24" data-testid="checkout-summary">
            <h3 className="font-bold" style={{ fontFamily: b.heading_font }}>Order Summary</h3>
            <div className="flex gap-2"><Input placeholder="Coupon (try WELCOME20)" value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} data-testid="checkout-coupon-input" /></div>
            {user?.role === "customer" && rewards.points > 0 && (
              <div>
                <Label className="text-xs">Use reward points (you have {rewards.points})</Label>
                <Input type="number" max={rewards.points} min={0} value={usePoints} onChange={(e) => setUsePoints(Math.max(0, Math.min(rewards.points, +e.target.value)))} />
              </div>
            )}
            <div className="text-sm space-y-1 border-t border-slate-200 pt-2">
              <div className="flex justify-between"><span>Subtotal</span><span>{inr(subtotal)}</span></div>
              {pointsValue > 0 && <div className="flex justify-between text-emerald-700"><span>Points credit</span><span>− {inr(pointsValue)}</span></div>}
              <div className="flex justify-between"><span>Refundable Deposit</span><span>{inr(depositTotal)}</span></div>
              <div className="flex justify-between text-lg font-bold pt-2" style={{ color: b.primary_color }}><span>Total</span><span>{inr(total)}</span></div>
            </div>
            <Button className="w-full t-btn" onClick={place} disabled={submitting} style={{ background: b.primary_color, color: "#fff" }} data-testid="checkout-pay-btn">
              <CreditCard className="h-4 w-4 mr-2" /> {submitting ? "Processing..." : `Pay ${inr(total)} (Mock)`}
            </Button>
            {!user && <p className="text-xs opacity-60">You'll be asked to verify your phone before placing the order.</p>}
          </Card>
        </div>
      )}
    </div>
  );
}
